import { identify as analyticsIdentify, reset as analyticsReset } from '@/lib/analytics';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth, tokenStore } from '@/api/client';
import { flushPending as flushPendingConsent } from '@/lib/consents';   // r7.6 consent ticked before the session existed (OAuth sign-up)

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Hydrate from stored token on mount
  useEffect(() => {
    const token = tokenStore.access;
    if (!token) { setLoading(false); return; }
    auth.me()
      .then((data) => { setUser(data?.user || data); flushPendingConsent().catch(() => {}); })
      .catch(() => { tokenStore.clear(); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  // Listen for global auth-failure event (from API client interceptor)
  useEffect(() => {
    function onUnauth() { setUser(null); }
    window.addEventListener('auth:unauthenticated', onUnauth);
    return () => window.removeEventListener('auth:unauthenticated', onUnauth);
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await auth.login({ email, password });
    tokenStore.set({
      accessToken: r.accessToken || r.access_token,
      refreshToken: r.refreshToken || r.refresh_token,
    });
    setUser(r.user);
    return r.user;
  }, []);

  const signup = useCallback(async (payload) => {
    const r = await auth.signup(payload);
    tokenStore.set({
      accessToken: r.accessToken || r.access_token,
      refreshToken: r.refreshToken || r.refresh_token,
    });
    setUser(r.user);
    return r.user;
  }, []);

  // r6: LoginPage/OAuth/biometric flows complete a session with the raw /auth/* response.
  // Was destructured by LoginPage but never provided → "setSession is not a function" after every successful login.
  const setSession = useCallback((data) => {
    if (!data) return;
    const accessToken = data.accessToken || data.access_token || data.tokens?.accessToken;
    const refreshToken = data.refreshToken || data.refresh_token || data.tokens?.refreshToken;
    if (accessToken) tokenStore.set({ accessToken, refreshToken });
    if (data.user) { setUser(data.user); analyticsIdentify(data.user); }
    return data.user || null;
  }, []);

  // r7.6.2 — re-read GET /me after a profile edit / verification so every consumer sees the new fields
  const refreshUser = useCallback(async () => {
    try { const data = await auth.me(); const u = data?.user || data; setUser(u); return u; } catch { return null; }
  }, []);

  const logout = useCallback(async () => {
    try { await auth.logout(); } catch { /* ignore — clear locally regardless */ }
    tokenStore.clear();
    setUser(null);
    analyticsReset();
    import('@/lib/capacitor').then(m => m.disableBiometricLogin?.()).catch(() => {});
  }, []);

  // r7.5 — one role model. `role` is the identity role (canonical spelling `superadmin`; the old
  // 'super_admin' check here matched nothing the server ever issued). Console roles (ops/support/
  // finance/content) and the effective `permissions` list come from GET /me, computed server-side
  // from the shared matrix — the shell gates nav/routes on permissions, never on role strings.
  const role = user?.role === 'super_admin' ? 'superadmin' : user?.role;
  const isAdminIdentity = !!user && (role === 'admin' || role === 'superadmin');
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const consoleRoles = Array.isArray(user?.roles) ? user.roles : [];
  // r7.6: superadmin-only codes (theme.palette, console.roles.grant_admin) are stripped from an admin's
  // effective list server-side, so `can()` must not short-circuit them through isAdminIdentity.
  const SUPERADMIN_ONLY = ['theme.palette', 'console.roles.grant_admin'];
  const can = (...codes) => codes.some((c) => (isAdminIdentity && !SUPERADMIN_ONLY.includes(c)) || permissions.includes(c) || role === 'superadmin');
  // r7.6: tenant memberships from GET /me (tenant.tenant_members owner|admin) — unlock the tenant console
  const tenantMemberships = Array.isArray(user?.tenant_memberships) ? user.tenant_memberships : [];
  const isTenantMember = tenantMemberships.length > 0;
  const isOrganizer = !!user && (role === 'organizer' || isAdminIdentity);
  const isConsole = isAdminIdentity || can('console.access');
  const value = {
    user, loading,
    login, signup, logout, setSession, refreshUser,   // r7.6.2 refreshUser
    isLoggedIn: !!user,
    isOrganizer,
    isAdmin: isAdminIdentity,                         // identity admin (theme lock, role grants UI)
    isSuperAdmin: role === 'superadmin',              // r7.6 palette editor, admin grants
    isConsole,                                        // may open /admin at all
    consoleRoles,
    permissions,
    can,
    role,
    tenantMemberships, isTenantMember,               // r7.6
    // r7.6 — where this account lands after login when no ?next= was given (role-aware redirect)
    landing: landingFor({ isConsole, isOrganizer, isTenantMember }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * r7.6 — post-login landing by role. Console staff → /admin (AdminIndex picks the first permitted section);
 * organizers → /organizer; a tenant owner/admin who is not an organizer → /organizer/branding; everyone
 * else → home. A `?next=` on the login page always wins over this.
 */
export function landingFor({ isConsole, isOrganizer, isTenantMember }) {
  if (isConsole) return '/admin';
  if (isOrganizer) return '/organizer';
  if (isTenantMember) return '/organizer/branding';
  return '/';
}

/** r7.6 — same rule, computed from a raw user object (the /auth/* response) before the context re-renders. */
export function landingForUser(user) {
  if (!user) return '/';
  const role = user.role === 'super_admin' ? 'superadmin' : user.role;
  const isAdminIdentity = role === 'admin' || role === 'superadmin';
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  return landingFor({
    isConsole: isAdminIdentity || permissions.includes('console.access'),
    isOrganizer: role === 'organizer' || isAdminIdentity,
    isTenantMember: Array.isArray(user.tenant_memberships) && user.tenant_memberships.length > 0,
  });
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
